import type { Website } from "@/constants/features";
import * as api from "../client";
import type {
  ContentStatus,
  LawyerProfile,
  ListResponse,
  Profession,
  RegionAssignmentInput,
  SocialAccountType,
  SocialProvider,
  StatusUpdatePayload,
} from "./types";

const BASE = "/v1/profiles/lawyer-profiles";

export type LawyerProfileFilters = {
  companyId?: string;
  status?: string;
  /** Free-text search (passed as `q`). API may not yet implement. */
  q?: string;
  /** Restrict to a single profession (`lawyer` | `barrister` | `clerk`). */
  profession?: Profession;
  /**
   * Region filter (§3.4): `regionKind` + `regionId` (integer) together or
   * neither. `regionId` is the integer `api_region_country_v2` id.
   */
  regionKind?: "region" | "country";
  regionId?: string;
  limit?: number;
  offset?: number;
};

export type LawyerProfileCreatePayload = {
  companyId: string;
  /** Address-book contact UUID. Required by profiles-api for lawyer writes. */
  contactId: string;
  firstName: string;
  lastName: string;
  profession: Profession;
  title?: string;
  position?: string;
  workDepartment?: string;
  telephone?: string;
  email?: string;
  photoHash?: string | null;
  photoFilename?: string | null;
  languageCode?: string;
  /** Publishing target / edition (Issue 3 / Issue 12). Defaults server-side to `com_en`. */
  website?: Website;
  career?: string;
  education?: string;
  personal?: string;
  leisure?: string;
  memberships?: string;
  /** Clerk-only editorial rich-text fields. */
  notableBarristers?: string;
  currentBarristers?: string;
  legal500Quotes?: string;
  status?: ContentStatus;
  socialMedia?: Array<{
    provider: SocialProvider;
    accountType: SocialAccountType;
    url: string;
    sortOrder: number;
  }>;
  languagesSpoken?: Array<{ languageCode: string; sortOrder: number }>;
  /** At least one region required server-side. */
  regions?: RegionAssignmentInput[];
};

export type LawyerProfileUpdatePayload = Partial<Omit<LawyerProfileCreatePayload, "companyId">>;

/** One language/site version in a batch group upsert (PUT .../groups/:groupId). */
export type LawyerProfileGroupVersion = Omit<LawyerProfileCreatePayload, "companyId"> & {
  /** Present = update this existing row; absent = create a new version. */
  id?: string;
};

export type LawyerProfileGroupUpsertPayload = {
  /** Required only when the group has no existing rows (fresh group). */
  companyId?: string;
  versions: LawyerProfileGroupVersion[];
};

export type LawyerProfileGroupResponse = {
  profileGroupId: string;
  lawyerProfiles: LawyerProfile[];
};

function buildQuery(filters: LawyerProfileFilters): string {
  const params = new URLSearchParams();
  if (filters.companyId) params.append("companyId", filters.companyId);
  if (filters.status) params.append("status", filters.status);
  if (filters.q) params.append("search", filters.q);
  if (filters.profession) params.append("profession", filters.profession);
  if (filters.regionKind && filters.regionId) {
    params.append("regionKind", filters.regionKind);
    params.append("regionId", filters.regionId);
  }
  if (filters.limit !== undefined) params.append("limit", String(filters.limit));
  if (filters.offset !== undefined) params.append("offset", String(filters.offset));
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const lawyerProfilesService = {
  async list(filters: LawyerProfileFilters = {}): Promise<ListResponse<LawyerProfile>> {
    const raw = await api.get<{
      lawyerProfiles: LawyerProfile[];
      pagination: { total: number; limit: number; offset: number };
    }>(`${BASE}${buildQuery(filters)}`);
    return {
      data: raw.lawyerProfiles ?? [],
      total: raw.pagination?.total ?? 0,
      limit: raw.pagination?.limit ?? 20,
      offset: raw.pagination?.offset ?? 0,
    };
  },

  async get(id: string): Promise<LawyerProfile> {
    const raw = await api.get<{ lawyerProfile: LawyerProfile } | LawyerProfile>(`${BASE}/${id}`);
    return (raw as { lawyerProfile?: LawyerProfile }).lawyerProfile ?? (raw as LawyerProfile);
  },

  create(payload: LawyerProfileCreatePayload): Promise<LawyerProfile> {
    return api.post<LawyerProfile>(BASE, payload);
  },

  update(id: string, payload: LawyerProfileUpdatePayload): Promise<LawyerProfile> {
    return api.put<LawyerProfile>(`${BASE}/${id}`, payload);
  },

  batchUpsertGroup(
    groupId: string,
    payload: LawyerProfileGroupUpsertPayload,
  ): Promise<LawyerProfileGroupResponse> {
    return api.put<LawyerProfileGroupResponse>(`${BASE}/groups/${groupId}`, payload);
  },

  remove(id: string): Promise<void> {
    return api.del<void>(`${BASE}/${id}`);
  },

  async updateStatus(id: string, payload: StatusUpdatePayload): Promise<LawyerProfile> {
    const raw = await api.patch<{ lawyerProfile: LawyerProfile } | LawyerProfile>(
      `${BASE}/${id}/status`,
      payload,
    );
    return (raw as { lawyerProfile?: LawyerProfile }).lawyerProfile ?? (raw as LawyerProfile);
  },
};
